/**
 * setupElderBiscuitAnchor — shared wiring helper for the Making Biscuits
 * system.
 *
 * Each Elder's level calls this once, next to where it spawns its Elder
 * NPC. The helper builds the anchor mesh, routes the activation key,
 * drives the minigame tick, shows proximity prompts, and handles the
 * "walk-away cancel" behavior so scene files don't have to duplicate
 * ~60 lines of boilerplate three times.
 *
 * The helper also owns the `VisionRenderer` and the
 * `MakingBiscuitsMinigame` instance, returning a handle the scene can
 * use to check activity state or plug into its own `onAbilityKey`
 * router.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";

import {
  ElderBiscuitAnchor,
  LeoSweaterAnchor,
  GusThrowAnchor,
  KittyBedrollAnchor,
} from "../entities/interactives/ElderBiscuitAnchor";
import { MakingBiscuitsMinigame } from "../abilities/minna/MakingBiscuitsMinigame";
import { VisionRenderer } from "../systems/VisionRenderer";
import { CutsceneSystem } from "../systems/CutsceneSystem";
import { AccessibilitySystem } from "../systems/AccessibilitySystem";
import { Minna } from "../entities/players/Minna";
import { BISCUITS } from "../config/GameConfig";
import { gameEvents } from "../core/EventBus";
import { GameEvents } from "../config/events";
import type { InteractiveInfo } from "../abilities/minna/AuraSight";
import { BiscuitElder } from "../systems/SaveSystem";

export interface BiscuitAnchorHandle {
  anchor: ElderBiscuitAnchor;
  minigame: MakingBiscuitsMinigame;
  visionRenderer: VisionRenderer;
  /**
   * Per-frame tick. Call from the scene's game loop AFTER physics but
   * before camera update — runs proximity check, minigame decay, and
   * the walk-away cancel.
   */
  update(dt: number): void;
  /**
   * Route an ability key through the biscuit system. Returns true when
   * the key was consumed (activation, cancel, or paw tap); scenes
   * should forward this before letting their own `onAbilityKey`
   * handlers run so the shared B/A/D bindings don't get stolen by
   * puzzle-specific code.
   */
  onAbilityKey(key: string, pressed: boolean, released: boolean): boolean;
  dispose(): void;
}

export interface SetupBiscuitAnchorOptions {
  scene: Scene;
  elder: BiscuitElder;
  minna: Minna;
  cutsceneSystem: CutsceneSystem;
  /**
   * World position of the anchor prop. Scenes typically place this
   * near the Elder's spawn so Minna discovers it post-encounter.
   */
  position: Vector3;
  /**
   * AuraSight interactive list — the helper pushes the anchor's
   * InteractiveInfo so it gets picked up by `auraSight.registerInteractives`.
   */
  interactiveRegistry: InteractiveInfo[];
  /**
   * Unique key for the anchor. Defaults to `"biscuitAnchor_{elder}"`.
   */
  key?: string;
}

export function setupElderBiscuitAnchor(
  opts: SetupBiscuitAnchorOptions,
): BiscuitAnchorHandle {
  const { scene, elder, minna, cutsceneSystem, position, interactiveRegistry } = opts;
  const key = opts.key ?? `biscuitAnchor_${elder}`;

  const minigame = new MakingBiscuitsMinigame();
  const visionRenderer = new VisionRenderer(scene, cutsceneSystem);

  // Pick the concrete anchor class based on Elder identity.
  let anchor: ElderBiscuitAnchor;
  const baseCfg = { key, position, elder, minigame, visionRenderer };
  if (elder === "leo") {
    anchor = new LeoSweaterAnchor(scene, baseCfg);
  } else if (elder === "gus") {
    anchor = new GusThrowAnchor(scene, baseCfg);
  } else {
    anchor = new KittyBedrollAnchor(scene, baseCfg);
  }

  interactiveRegistry.push({
    mesh: anchor.rootMesh,
    hidden: true,
    state: "hidden",
  });

  // Prompt bookkeeping: only show the prompt when Minna is in range
  // and the anchor has been revealed (Aura Sight has fired at least
  // once since the last conceal).
  let promptShownForAnchor = false;
  let inRange = false;

  // Suppress movement for A/D keys while a biscuit session is running
  // — otherwise the paw taps would also nudge Minna sideways.
  const update = (dt: number): void => {
    // Drive the minigame's decay + assist-fill clock.
    minigame.update(dt);

    // Range check — if Minna walks out of range while a session is
    // active, cancel the session.
    const wasInRange = inRange;
    inRange = anchor.isInInteractRange(minna.mesh.position);
    if (!inRange && minigame.isActive && minigame.activeAnchorKey === anchor.key) {
      minigame.cancel("movement");
    }

    // Prompt: show only when Minna is in range and the anchor has been
    // revealed (state transitions out of "hidden" once Aura Sight reveals).
    const revealed = anchor.state !== "hidden";
    if (inRange && revealed && !promptShownForAnchor && !minigame.isActive) {
      promptShownForAnchor = true;
      gameEvents.emit(GameEvents.HUD_SHOW_PROMPT, {
        text: `Minna: press ${BISCUITS.ACTIVATION_KEY.toUpperCase()} to knead the ${labelFor(elder)}. (Auto-Knead assist available in Pause menu.)`,
      });
    } else if ((!inRange || !revealed) && promptShownForAnchor) {
      promptShownForAnchor = false;
      // Scene-level prompts own HUD_HIDE_PROMPT; we avoid emitting
      // unconditionally so the puzzle-hint prompt can re-show after
      // the anchor prompt clears. The scene's refresh loop will
      // restore its own text on the next cycle.
      gameEvents.emit(GameEvents.HUD_HIDE_PROMPT);
    }

    // Basil can't knead — if the minigame got stuck (defensive) when
    // Minna wasn't actually next to the anchor, clean up.
    if (minigame.isActive && !inRange) {
      minigame.cancel("movement");
    }
  };

  const onAbilityKey = (
    key: string,
    pressed: boolean,
    _released: boolean,
  ): boolean => {
    if (!pressed) return false;

    // Activation: `b` near the anchor enters biscuit mode. A second
    // press while active cancels without consuming the vision.
    if (key === BISCUITS.ACTIVATION_KEY) {
      if (minigame.isActive && minigame.activeAnchorKey === anchor.key) {
        minigame.cancel("cancel");
        return true;
      }
      const assistMode = AccessibilitySystem.getInstance().state.autoKneadAssist;
      return anchor.tryActivate(minna.mesh.position, { assistMode });
    }

    // Paw taps while active. We only consume the key if a session is
    // actively running AT THIS anchor — otherwise A/D drive movement.
    if (minigame.isActive && minigame.activeAnchorKey === anchor.key) {
      if (key === BISCUITS.LEFT_PAW_KEY) {
        minigame.pressPaw("left");
        return true;
      }
      if (key === BISCUITS.RIGHT_PAW_KEY) {
        minigame.pressPaw("right");
        return true;
      }
    }

    return false;
  };

  const dispose = (): void => {
    try {
      anchor.dispose();
    } catch (err) {
      console.warn("[BiscuitAnchor] anchor dispose error", elder, err);
    }
    try {
      minigame.dispose();
    } catch {
      /* no-op */
    }
    try {
      visionRenderer.dispose();
    } catch {
      /* no-op */
    }
  };

  return {
    anchor,
    minigame,
    visionRenderer,
    update,
    onAbilityKey,
    dispose,
  };
}

function labelFor(elder: BiscuitElder): string {
  if (elder === "leo") return "argyle sweater";
  if (elder === "gus") return "woven throw";
  return "bedroll";
}
